import type { CommandCenterMention } from "@/services/command-center";
import { AI_KEY_MISSING_CODE, AI_KEY_SETTINGS_LINK_LABEL, AI_KEY_SETTINGS_PATH } from "@/lib/ai-key";

export const COMMAND_CENTER_COPY = {
  title: "Espaço da equipe",
  lede: "Converse com sua equipe e acompanhe o que precisa de você.",
  empty: "Comece uma conversa com sua equipe.",
  loadError: "Não foi possível carregar o espaço da equipe.",
  retry: "Tentar de novo",
  realtimeOff: "Tempo real indisponível; a lista atualiza a cada 30 segundos.",
  realtimeOn: "conectado",
  realtimeDegraded: "atualiza a cada 30 s",
  composerLabel: "Mensagem",
  composerPlaceholder: "Escreva para a equipe…",
  composerRecipientHelp: "Use @ para chamar uma pessoa ou agente.",
  attendanceOffMentions: "O atendimento automático está desligado. Os agentes estão pausados; o Copiloto continua disponível.",
  attendanceUnknownMentions: "Não foi possível confirmar se os agentes podem responder. Você pode falar com a equipe ou usar @copiloto.",
  replaceAgentMentions: "Perguntar ao Copiloto",
  replaceTodosWarning: "Ao trocar @todos por @copiloto, esta mensagem deixa de notificar toda a clínica.",
  send: "Enviar",
  sendError: "Não foi possível enviar. Tente de novo.",
  limit: 4000,
  openHitl: "Ver solicitação",
  openConversation: "Abrir conversa",
  participants: "Participantes",
  participantsSearch: "Buscar participante",
  participantsEmpty: "Nenhum participante encontrado.",
  peopleTitle: "Pessoas da clínica",
  presenceUnknown: "presença não confirmada",
  agentsSoon: "Responde quando mencionado",
  agentsUnavailable: "Equipe de IA indisponível no momento",
  agentsTitle: "Equipe de IA",
  briefing: "Briefing diário",
  loadMore: "Carregar mensagens anteriores",
  system: "Sistema",
  mentionListLabel: "Participantes",
  mentionTodosHint: "Avisa a clínica e solicita uma resposta do Coordenador",
  mentionsDegraded: "Não deu para checar as menções a agentes agora.",
  mentionDotLabel: "Você foi mencionado no Espaço da equipe",
  unreadBadgeLabel: (count: number) =>
    count === 1
      ? "1 mensagem não lida no Espaço da equipe"
      : `${count} mensagens não lidas no Espaço da equipe`,
  mentionToastTitle: "Espaço da equipe",
  mentionToastBody: (name: string) => `${name} mencionou você no Espaço da equipe`,
  mentionToastAction: "Abrir",
} as const;

const AGENT_REPLY_REASONS = new Set([
  "quota_exceeded", "engine_unreachable", "engine_rejected", "attendance_disabled",
  "attendance_unavailable", "agent_failed", "timeout", "expired", "copilot_unavailable",
  "copilot_error", "needs_approval", "agent_not_found", "agent_unavailable", "empty_answer",
  "budget_daily", "budget_monthly", "budget_steps", "budget_tool_calls", "budget_tokens",
  "budget_usd", "task_cancelled", "policy_unavailable",
  "attendant_unavailable", "attendant_error", "ai_key_missing",
]);

/** Laravel owns the complete masked sentence. Unknown codes never expose diagnostic content. */
export function agentReplyFailureText(content: string, reason: string | null): string {
  return reason && AGENT_REPLY_REASONS.has(reason) && content.trim()
    ? content : "Não foi possível concluir a resposta. Confira o histórico antes de continuar o trabalho manualmente.";
}

/**
 * Navigation only: none of these routes repeats an attempt or assumes a request exists.
 * Only the Copilot's own bubbles name the Copilot. The hourly reply cap applies to every
 * agent; the daily/monthly usage limits have no clinic-facing control outside the Copilot
 * page, so another agent's bubble gets no link for them.
 */
export function agentReplyRecoveryLink(reason: string | null, admin: boolean, copilot: boolean): { to: string; label: string } | null {
  if (reason === "needs_approval") return { to: "/growth-team/attention", label: "Consultar solicitações" };
  // TENANT-AIKEY-REQUIRED-01: only an admin reaches the AI settings.
  if (reason === AI_KEY_MISSING_CODE) return admin ? { to: AI_KEY_SETTINGS_PATH, label: AI_KEY_SETTINGS_LINK_LABEL } : null;
  if (reason === "policy_unavailable" && admin) return { to: "/growth-team/rules", label: "Consultar autonomia" };
  if (["attendance_disabled", "attendance_unavailable", "agent_not_found", "agent_unavailable", "policy_unavailable"].includes(reason ?? "")) {
    return { to: "/growth-team", label: "Consultar configurações da IA" };
  }
  if (!copilot && reason === "quota_exceeded") return { to: "/settings/automations", label: "Consultar limite de respostas" };
  if (copilot && ["quota_exceeded", "budget_daily", "budget_monthly"].includes(reason ?? "")) {
    return { to: "/settings/automations", label: "Consultar configurações do Copiloto" };
  }
  return null;
}

export function agentReplyNeedsCopilotReview(reason: string | null): boolean {
  return ["budget_steps", "budget_tool_calls", "budget_tokens", "budget_usd", "task_cancelled"].includes(reason ?? "");
}

export function ambiguousMentionTitle(mention: CommandCenterMention): string {
  const names = (mention.candidates ?? []).map((candidate) => candidate.name).join(", ");
  return names === "" ? `Não sei quem é ${mention.token}` : `Não sei quem é ${mention.token}: ${names}`;
}
